import type { Role } from '@/stores/session';

/** Roles allowed to see deal monetary values — mirrors crm MONEY_VISIBLE_ROLES (C7). */
const MONEY_VISIBLE_ROLES: ReadonlySet<Role> = new Set([
  'super_admin',
  'founder',
  'sales_manager',
  'accountant',
]);

export function canSeeMoney(role: Role | null): boolean {
  return role != null && MONEY_VISIBLE_ROLES.has(role);
}

/**
 * Roles allowed to *set* a deal's commission rate. The salesperson
 * (Sales Manager / Lead Gen) can see it but must not change it — their pay
 * rate is set by an admin/founder/accountant. Mirrors the backend guard.
 */
const COMMISSION_EDIT_ROLES: ReadonlySet<Role> = new Set([
  'super_admin',
  'founder',
  'accountant',
]);

export function canEditCommission(role: Role | null): boolean {
  return role != null && COMMISSION_EDIT_ROLES.has(role);
}

/**
 * Some locales glue a letter-prefixed currency symbol to the amount (e.g.
 * `US$1,000.00`, `CA$50`). Add a space after the symbol for readability →
 * `US $1,000.00`. Leaves plain `$`/`€` and code-style (`USD 1,000`) untouched.
 */
function spaceCurrencySymbol(formatted: string): string {
  return formatted.replace(/([A-Za-z])\$/g, '$1 $');
}

/**
 * Format a monetary amount + currency for display. The backend returns DecimalField
 * amounts as strings (e.g. "1000.00"), and `null` when the value is unset OR masked
 * server-side for the current role (F3/F4). Callers decide how to render `null`.
 */
export function formatMoney(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
  options?: {
    /** Use the short symbol ("$" instead of "US$"). */
    narrowSymbol?: boolean;
  },
): string | null {
  if (amount == null || currency == null) return null;
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(n)) return null;
  try {
    return spaceCurrencySymbol(
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        ...(options?.narrowSymbol ? { currencyDisplay: 'narrowSymbol' as const } : {}),
      }).format(n),
    );
  } catch {
    return `${n.toLocaleString()} ${currency}`;
  }
}

/**
 * Compact money for dense UIs — e.g. `$1.2K`, `€950`, `$3M`. Used for column
 * roll-ups on the pipeline board. Returns `null` for unset/masked values.
 */
export function formatMoneyCompact(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
): string | null {
  if (amount == null || currency == null) return null;
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(n)) return null;
  try {
    return spaceCurrencySymbol(
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        notation: 'compact',
      }).format(n),
    );
  } catch {
    return `${n.toLocaleString()} ${currency}`;
  }
}
